import { useQuery } from '@apollo/client';
import capitalize from 'lodash/capitalize';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import {
  TransactionAccount,
  FetchLinkedEntitlementsQuery,
  FetchLinkedEntitlementsQueryVariables,
  LinkedDestinationEntitlement,
  LinkedEntitlementTypes,
  LinkedProductTypes,
  Maybe,
  BillingFrequency,
} from '@css-xp/graphtypes/src/generated-types-new';
import { R300 } from '@atlaskit/theme/colors';
import { DynamicResultTable, NoDataContentInTable, entitlementUtils, FormattedAddress } from '@css-xp/base';
import {
  CloudProductDetailView,
  EntitlementStatus,
  getBilledForColumnData,
  NextBillExpiryDate,
} from '@css-xp/base/entitlement-utils';
import { NO_DETAILS_TO_SHOW } from '@atlassian/cssxp-utils-entitlement';
import React, { useCallback } from 'react';
import { fetchLinkedEntitlements } from '../../queries/fetch-linked-entitlements';
import { useTransactionAccountContext } from '@css-xp/base/common-context';

export const emptyStateOrderProp = {
  header: '',
  description: NO_DETAILS_TO_SHOW,
  imageUrl: '',
  testId: 'no-parents-entitlements-state',
};

export interface ParentsEntitlementTabProps {
  id: string | undefined;
  currentEntitlementType?: LinkedEntitlementTypes;
  destinationEntitlementType: Array<LinkedEntitlementTypes>;
  productType: Maybe<LinkedProductTypes> | undefined;
  findProducts?: boolean;
  findApps?: boolean;
  entitlementId: string;
}

const ParentsEntitlementTab = ({
  id,
  currentEntitlementType,
  destinationEntitlementType,
  productType,
  findProducts,
  findApps,
  entitlementId,
}: ParentsEntitlementTabProps) => {
  const { data, loading, error } = useQuery<FetchLinkedEntitlementsQuery, FetchLinkedEntitlementsQueryVariables>(
    fetchLinkedEntitlements,
    {
      variables: {
        id: entitlementId,
        searchLinkedEntitlementParams: {
          entitlementId,
          containerId: id ?? null,
          currentEntitlementType,
          destinationEntitlementType,
          productType,
          findProducts,
          findApps,
        },
      },
      displayName: 'fetchParentEntitlements',
      notifyOnNetworkStatusChange: true,
    }
  );
  const parentEntitlements: Array<LinkedDestinationEntitlement> = data?.entitlementDetails
    .linkedEntitlements as Array<LinkedDestinationEntitlement>;

  const { data: transactionAccountData, getTransactionAccount } = useTransactionAccountContext();
  const transactionAccount = getTransactionAccount(transactionAccountData);

  const getErrorState = React.useCallback((error: string | undefined) => {
    const renderImage = () => <WarningIcon label="Error" size="xlarge" primaryColor={R300} />;
    return {
      header: '',
      description: (
        <span className="error-view">Oh no! Something went wrong, please try again. {error && error.toString()}</span>
      ),
      renderImage,
    };
  }, []);

  const getSoldToAddress = useCallback(
    (invoiceGroupId: string | undefined | null) => {
      let currentInvoiceGroupDetails;
      if (!invoiceGroupId) {
        currentInvoiceGroupDetails = transactionAccount?.availableInvoiceGroups?.nodes[0];
      } else {
        currentInvoiceGroupDetails = transactionAccount?.availableInvoiceGroups?.nodes?.find(
          (invoiceGroupObj) => invoiceGroupObj?.id === invoiceGroupId
        );
      }
      if (currentInvoiceGroupDetails && currentInvoiceGroupDetails?.shipToParty) {
        return (
          <div className="line-clamp-sold-to-address">
            <FormattedAddress
              addressLine1={currentInvoiceGroupDetails?.shipToParty?.addressLine1}
              addressLine2={currentInvoiceGroupDetails?.shipToParty?.addressLine2}
              city={currentInvoiceGroupDetails?.shipToParty?.city}
              country={currentInvoiceGroupDetails?.shipToParty?.country}
              postcode={currentInvoiceGroupDetails?.shipToParty?.postcode}
              state={currentInvoiceGroupDetails?.shipToParty?.state}
              organizationName={currentInvoiceGroupDetails?.shipToParty?.name}
            />
          </div>
        );
      }
      return 'No address information';
    },
    [transactionAccount]
  );

  const getBillToAddress = useCallback(
    (transactionAccountId: string | undefined | null) => {
      if (!transactionAccountId || !transactionAccount?.billToParty) {
        return 'No address information';
      }
      const billToParty = transactionAccount.billToParty;
      return (
        <span className="line-clamp">
          <FormattedAddress
            addressLine1={billToParty?.addressLine1}
            addressLine2={billToParty?.addressLine2}
            city={billToParty?.city}
            country={billToParty?.country}
            postcode={billToParty?.postcode}
            state={billToParty?.state}
          />
        </span>
      );
    },
    [transactionAccount]
  );

  const getRows = () => {
    return (parentEntitlements ?? [])
      .filter((entitlement) => entitlement.entitlementId !== entitlementId)
      .map((item, index) => {
        return {
          key: index.toString(),
          cells: [
            {
              content: (
                <CloudProductDetailView
                  productId={item?.productId as string}
                  entitlementNumber={item.entitlementNumber ?? ''}
                  provisioningStatus={item?.provisioningStatus}
                  edition={item?.edition}
                  productName={item?.productName}
                  cloudSiteId={item?.cloudSiteId}
                  trial={item?.trial ?? false}
                  upgradeTrial={item?.upgradeTrial}
                  billingStatus={item.billingStatus}
                  scheduledChanges={item.scheduledChanges}
                />
              ),
            },
            {
              content: item.billingStatus ? (
                <EntitlementStatus billingStatus={item.billingStatus} provisioningStatus={item?.provisioningStatus} />
              ) : (
                <NoDataContentInTable />
              ),
            },
            {
              content: (
                <div>
                  <div>{capitalize(getBilledForColumnData(item?.productId, item?.edition))}</div>
                  <div>{item?.activeUsers ?? '0'}</div>
                </div>
              ),
            },
            {
              content: <NextBillExpiryDate item={item} />,
            },
            {
              content: (
                <>
                  <p>{item?.billingFrequency ? capitalize(item.billingFrequency) : <NoDataContentInTable />}</p>
                  {item?.billingFrequency === BillingFrequency.ANNUAL && item?.userTier && (
                    <p data-testid="linked-ent-user-tier">User tier - {item?.userTier}</p>
                  )}
                </>
              ),
            },
            {
              content: (
                <entitlementUtils.PaymentMethodView
                  invoiceGroupId={item?.billingGroupId}
                  transactionAccount={transactionAccount as TransactionAccount}
                />
              ),
            },
            {
              content: getBillToAddress(item?.transactionAccountId),
            },
            {
              content: getSoldToAddress(item?.billingGroupId),
            },
          ],
        };
      });
  };

  const getHead = () => {
    const cells = [
      {
        content: 'Subscription',
        shouldTruncate: true,
        width: 25,
      },
      {
        content: 'Status',
        width: 10,
      },
      {
        content: 'Usage',
        width: 10,
      },
      {
        content: 'Next bill/Expiry',
        width: 10,
      },
      {
        content: 'Billing cycle',
        width: 10,
      },
      {
        content: 'Payment method',
        width: 15,
      },
      {
        content: 'Billing address',
        width: 15,
      },
      {
        content: 'Sold-to address',
        width: 15,
      },
    ];
    return {
      cells,
    };
  };

  return (
    <div className="statuspage-tab" data-testid="statuspage-add-ons">
      <DynamicResultTable
        testId="parents-entitlement-tab"
        headingText="Parent subscriptions"
        head={getHead()}
        rows={getRows()}
        emptyStateProps={emptyStateOrderProp}
        isLoading={loading}
        errorStateProps={getErrorState(error?.message.toString())}
        error={error?.message}
      />
    </div>
  );
};

export default ParentsEntitlementTab;
